import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const products = [
  {
    id: "pvc-walls",
    name: "PVC Warehouse Dividing Walls",
    desc: "Heavy-duty PVC partition walls designed to divide large warehouse spaces into functional zones. Perfect for temperature control, dust containment, and workflow separation.",
    img: "https://customer-assets.emergentagent.com/job_industrial-textiles-1/artifacts/ewmocaic_IMG_4691.JPEG",
    benefits: ["Temperature zone control", "Dust and debris containment", "Noise reduction", "Flexible workspace division"],
    applications: ["Distribution centres", "Cold storage facilities", "Manufacturing plants", "Food processing"],
    specs: "PVC 900gsm - 1200gsm, UV stabilized, fire retardant to AS 1530"
  },
  {
    id: "sliding-curtains",
    name: "Industrial Sliding Curtains",
    desc: "Robust sliding curtain systems that provide flexible access while maintaining environmental separation. Engineered for high-traffic industrial environments.",
    img: "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/a8cc9264b68745bb769ea851b3303608393efc5707361b86599777931788f6aa.png",
    benefits: ["Easy operation", "High-traffic durability", "Weather sealing", "Quick access control"],
    applications: ["Loading docks", "Vehicle wash bays", "Paint booths", "Warehouse openings"],
    specs: "Heavy-duty track systems, PVC or vinyl fabric, wind-rated options"
  },
  {
    id: "industrial-covers",
    name: "Industrial Covers",
    desc: "Custom-manufactured protective covers for machinery, equipment, and materials. Built to withstand UV exposure, rain, dust, and extreme temperatures.",
    img: "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/c6219fe4f1590063ac540153a1f2177151af5acdf85baf614f322b307bf27f05.png",
    benefits: ["UV and weather protection", "Custom fit to any shape", "Durable construction", "Easy on/off design"],
    applications: ["Machinery protection", "Stockpile covers", "Vehicle covers", "Equipment storage"],
    specs: "PVC, canvas, or poly fabric. Custom sizes. Reinforced seams and eyelets."
  },
  {
    id: "welding-screens",
    name: "Welding Screens",
    desc: "Purpose-built welding safety screens that protect workers from harmful UV radiation, sparks, and spatter. Available in fixed and portable configurations.",
    img: "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/86d0c5b450f80735c70910374ac2d2b270c24c5559a77b8c219deef71f9de151.png",
    benefits: ["UV radiation protection", "Spark containment", "Portable options", "Clear vision panels"],
    applications: ["Welding bays", "Metal fabrication shops", "Construction sites", "Maintenance workshops"],
    specs: "EN 1598 certified, flame retardant PVC, steel frame options"
  },
  {
    id: "weather-protection",
    name: "Weather Protection Systems",
    desc: "Comprehensive weather protection solutions for open-sided buildings, loading areas, and outdoor workspaces. Designed to handle Australian weather extremes.",
    img: "https://images.unsplash.com/photo-1771970472779-6452fbce94d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc5Njc3Mjg4fDA&ixlib=rb-4.1.0&q=85",
    benefits: ["Rain and wind protection", "UV shading", "Ventilation options", "Retractable designs"],
    applications: ["Loading docks", "Outdoor work areas", "Farm buildings", "Sports facilities"],
    specs: "UV-stabilized PVC, wind-rated to 120km/h, reinforced edges"
  },
  {
    id: "custom-textile",
    name: "Custom Textile Solutions",
    desc: "Bespoke industrial textile products designed and manufactured to your unique specifications. If you can imagine it, we can build it.",
    img: "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/e6a2ebf2cd313ea73335a4e06d512892e00e50d6fe629b682a60a94b0e1914c9.png",
    benefits: ["Fully custom design", "Material selection", "Expert consultation", "Prototype development"],
    applications: ["Specialty enclosures", "Custom containment", "Unique industrial needs", "Retrofit solutions"],
    specs: "Multiple materials available. Custom specifications on request."
  },
];

export default function ProductsPage() {
  return (
    <div data-testid="products-page">
      {/* Hero */}
      <section className="relative py-32 sm:py-40 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="industrial-line mb-8" />
            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Our <span className="gradient-text">Products</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg text-[#A3A3A3] max-w-xl leading-relaxed">
              Commercial-grade industrial textile products engineered for performance, 
              durability, and reliability in the most demanding environments.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Products List */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              data-testid={`product-${product.id}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 !== 0 ? "lg:direction-rtl" : ""
              }`}
              style={index % 2 !== 0 ? { direction: "rtl" } : {}}
            >
              <motion.div variants={fadeUp} style={{ direction: "ltr" }}>
                <div className="overflow-hidden">
                  <img
                    src={product.img}
                    alt={product.name}
                    className="w-full aspect-[16/10] object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </motion.div>
              <motion.div variants={fadeUp} style={{ direction: "ltr" }}>
                <div className="industrial-line mb-6" />
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                  {product.name}
                </h2>
                <p className="text-[#A3A3A3] mb-6 leading-relaxed">{product.desc}</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>Key Benefits</h4>
                    <ul className="space-y-2">
                      {product.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-[#A3A3A3]">
                          <CheckCircle size={14} className="text-[#FF5722] mt-0.5 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>Applications</h4>
                    <ul className="space-y-2">
                      {product.applications.map((a) => (
                        <li key={a} className="flex items-start gap-2 text-sm text-[#A3A3A3]">
                          <CheckCircle size={14} className="text-[#FF5722] mt-0.5 shrink-0" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-[#141414] border border-[#2E2E2E] p-4 mb-6">
                  <p className="text-xs text-[#A3A3A3]">
                    <span className="text-[#FF5722] font-semibold">Material Specs:</span> {product.specs}
                  </p>
                </div>

                <Link
                  to="/quote"
                  data-testid={`product-quote-${product.id}`}
                  className="inline-flex items-center gap-2 bg-[#FF5722] text-white px-6 py-3 font-semibold text-sm hover:bg-[#E64A19] transition-colors group"
                >
                  Request Quote <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
